import { useState, useRef, useCallback } from "react";
import { Tabs } from "shared/components/BaseTabs";
import { EditorRefApi } from "shared/components/Editor";
import { LiteTextEditor } from "shared/app/Editor/LiteTextEditor";
import { isTextEmpty } from "shared/app/ActivityLog/helpers";
import { CommunicationMetadata } from "./CommunicationMetadata";
import { type ComposerTab } from "./constants";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import { useCreateManualNote, inboxKeys } from "../api/hooks";
import { useInboxContext } from "../context/useInboxContext";
import { useEnsureConversation } from "../hooks/useEnsureConversation";
import { useComposerTabs } from "../hooks/useComposerTabs";

interface ManualCommunicationFormProps {
  conversationId?: string;
  contactId?: string;
  draftContactId?: number;
  /** Selected channel. Owned by the host: `?tab=` on /inbox (see MessageComposer). */
  activeTab: ComposerTab;
  onTabChange: (tab: ComposerTab) => void;
}

export const ManualCommunicationForm = ({
  conversationId,
  contactId,
  draftContactId,
  activeTab,
  onTabChange
}: ManualCommunicationFormProps) => {
  const { visibleTabs, inviteModal } = useComposerTabs(contactId);
  const editorRef = useRef<EditorRefApi>(null);
  const [communicationType, setCommunicationType] = useState("Call");
  const [isEmpty, setIsEmpty] = useState(true);
  const [communicationDate, setCommunicationDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [communicationTime, setCommunicationTime] = useState(() => {
    const now = new Date();
    const hours = now.getHours().toString().padStart(2, "0");
    const minutes = now.getMinutes().toString().padStart(2, "0");
    return `${hours}:${minutes}`;
  });

  const { mutate: createNote, isPending } = useCreateManualNote();
  const queryClient = useQueryClient();
  const {
    actions: { clearTargetMessage, selectConversation }
  } = useInboxContext();
  const ensureConversation = useEnsureConversation(
    conversationId,
    draftContactId
  );

  const handleTabChange = useCallback(
    (tab: ComposerTab) => {
      clearTargetMessage();
      onTabChange(tab);
    },
    [onTabChange, clearTargetMessage]
  );

  const handleRecordCommunication = async () => {
    if (!conversationId && draftContactId === undefined) {
      toast.error("No conversation selected");
      return;
    }

    const doc = editorRef.current?.getDocument();
    if (!doc || isEmpty) {
      toast.error("Please add communication notes");
      return;
    }

    const dateTimeString = `${communicationDate}T${communicationTime}:00`;
    const isoDate = new Date(dateTimeString).toISOString();

    let resolvedConversationId: string;
    try {
      resolvedConversationId = await ensureConversation();
    } catch {
      toast.error("Failed to start conversation");
      return;
    }

    const wasDraft = draftContactId !== undefined && !conversationId;

    createNote(
      {
        conversation_id: resolvedConversationId,
        type: communicationType,
        date: isoDate,
        content_plain: doc.text,
        content_html: doc.html
      },
      {
        onSuccess: () => {
          toast.success("Communication recorded successfully");
          editorRef.current?.clearEditor();
          setIsEmpty(true);
          if (wasDraft) {
            queryClient.invalidateQueries({
              queryKey: inboxKeys.conversations()
            });
            selectConversation(resolvedConversationId);
          }
        },
        onError: () => {
          toast.error("Failed to record communication");
        }
      }
    );
  };

  return (
    <div className="bg-white border-t border-alpha-100">
      <div className="px-6 py-4">
        <div className="flex flex-col gap-2">
          <LiteTextEditor
            ref={editorRef}
            id="communication-notes-editor"
            editable
            disableMentions
            placeholder="Communication notes (optional)"
            containerClassName="min-h-20 border border-alpha-100 py-3 px-4 rounded-lg text-sm shadow-xs font-medium"
            onChange={() => {
              const doc = editorRef.current?.getDocument();
              if (doc) setIsEmpty(isTextEmpty(doc.text));
            }}
          />
        </div>
        <CommunicationMetadata
          communicationType={communicationType}
          onTypeChange={setCommunicationType}
          communicationDate={communicationDate}
          onDateChange={setCommunicationDate}
          communicationTime={communicationTime}
          onTimeChange={setCommunicationTime}
          onSubmit={handleRecordCommunication}
          isLoading={isPending}
        />
      </div>
      <div className="px-6 py-4 min-h-[80px] border-t border-alpha-40 flex items-center">
        <Tabs
          tabs={visibleTabs}
          active={activeTab}
          onChange={handleTabChange}
          tabStyle="ghost-pink"
          size="sm"
          fullWidthTabs={false}
          className="[&_button]:rounded-full"
        />
      </div>
      {inviteModal}
    </div>
  );
};
